import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RazorpayPayment from './RazorpayPayment';
import firebaseService from '../../services/firebaseService';

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, setPurchaseStatus } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [hardCopyOrdered, setHardCopyOrdered] = useState(false);

  const accessType = searchParams.get('type');
  const isOnline = accessType === 'online';

  useEffect(() => {
    // Redirect if not authenticated
    if (!user) {
      navigate('/');
      return;
    }

    // Pre-fill form with user data
    setFormData(prev => ({
      ...prev,
      name: user.name || '',
      email: user.email || ''
    }));

    // Load stored payment info if exists
    const storedInfo = localStorage.getItem('fijoli_payment_info');
    if (storedInfo) {
      const parsedInfo = JSON.parse(storedInfo);
      setFormData(prev => ({
        ...prev,
        name: parsedInfo.name || user.name || '',
        email: parsedInfo.email || user.email || ''
      }));
    }
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    // For offline access, validate address fields
    if (!isOnline) {
      if (!formData.address || !formData.city || !formData.state || !formData.pincode) {
        alert('Please fill in all address fields for offline delivery');
        return;
      }
    }

    setIsLoading(true);

    // Update stored payment info
    const paymentInfo = {
      ...formData,
      accessType: accessType,
      amount: isOnline ? 99 : 1499,
      userId: user.uid
    };

    localStorage.setItem('fijoli_payment_info', JSON.stringify(paymentInfo));

    // Show payment component
    setShowPayment(true);
    setIsLoading(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  if (!user) {
    return null;
  }

  if (hardCopyOrdered) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Order Confirmed!
            </h1>
            <p className="text-gray-600 mb-2">
              Your hard copy will be shipped to:
            </p>
            <p className="text-gray-800 font-medium mb-6">
              {formData.name}, {formData.address}, {formData.city}, {formData.state} - {formData.pincode}
            </p>
            <p className="text-gray-600 mb-8">
              We'll send delivery updates to {formData.phone}.
            </p>
            <button
              onClick={() => navigate('/reader-pageflip')}
              className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
            >
              Start Reading Online
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showPayment) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Complete Your Payment
              </h1>
              <p className="text-gray-600">
                {isOnline ? 'Online Access - ₹99 (early bird offer)' : 'Offline Access - ₹1,499'}
              </p>
            </div>

            <RazorpayPayment
              amount={isOnline ? 99 : 1499}
              onClose={() => setShowPayment(false)}
              onSuccess={async (response) => {
                // Handle successful payment
                try {
                  const amount = isOnline ? 99 : 1499;
                  const currentAccessType = isOnline ? 'web_access' : 'hard_copy';
                  // Buying the hard copy on top of web access gives both, not hard copy alone
                  const hadWebAccess = user.accessType === 'web_access' || user.accessType === 'both';
                  const newAccessType = !isOnline && hadWebAccess ? 'both' : currentAccessType;
                  const orderData = {
                    userId: user.uid,
                    userName: formData.name || user.name || 'Reader',
                    userEmail: formData.email || user.email,
                    product: currentAccessType,
                    amount: amount,
                    currency: 'INR',
                    razorpayPaymentId: response?.razorpay_payment_id || null,
                    whatsappNumber: formData.phone,
                    orderType: currentAccessType,
                    deliveryAddress: !isOnline ? {
                      name: formData.name,
                      address: formData.address,
                      city: formData.city,
                      state: formData.state,
                      pincode: formData.pincode
                    } : null,
                    status: 'completed',
                    paymentAmount: amount
                  };
                  const orderResult = !isOnline
                    ? await firebaseService.saveHardCopyOrder(orderData)
                    : await firebaseService.saveOrder(orderData);
                  if (!orderResult.success) {
                    console.error('Failed to save order in PaymentPage:', orderResult.error, response?.razorpay_payment_id);
                  }
                  const accessResult = await setPurchaseStatus(true, newAccessType, amount);
                  if (!accessResult.success) {
                    console.error('Failed to persist book access in PaymentPage:', accessResult.error, response?.razorpay_payment_id);
                  }
                } catch (err) {
                  console.error('Error recording payment in PaymentPage:', err);
                }
                localStorage.removeItem('fijoli_payment_info');
                if (isOnline) {
                  navigate('/reader-pageflip');
                } else {
                  setShowPayment(false);
                  setHardCopyOrdered(true);
                }
              }}
              onFailure={(error) => {
                alert('Payment failed. Please try again.');
                setShowPayment(false);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-8 text-center">
            <h1 className="text-4xl font-bold mb-4">
              {isOnline ? 'Online Access' : 'Offline Access'}
            </h1>
            <p className="text-xl opacity-90">
              {isOnline
                ? 'Complete your purchase to access Fijoli online'
                : 'Complete your purchase to get Fijoli delivered to your doorstep'
              }
            </p>
            <div className="mt-4 text-3xl font-bold flex items-center justify-center gap-3">
              {isOnline ? (
                <>
                  <span className="line-through text-white text-opacity-50 text-2xl font-normal">₹299</span>
                  <span>₹99</span>
                  <span className="text-sm font-semibold px-2 py-0.5 bg-white bg-opacity-20 rounded-full font-sans">
                    (early bird offer)
                  </span>
                </>
              ) : (
                <span>₹1,499</span>
              )}
            </div>
          </div>

          {/* Form */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              {/* Address Information - Only for Offline Access */}
              {!isOnline && (
                <>
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Delivery Address</h3>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Address *
                    </label>
                    <textarea
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Enter your complete address"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter city"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        required
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter state"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pincode *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        required
                        value={formData.pincode}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Enter pincode"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-6">
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back to Home
                </button>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Processing...' : `Proceed to Payment - ₹${isOnline ? '99' : '1,499'}`}
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default PaymentPage;

